import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Admin.css';

const Settings = () => {
  const [settings, setSettings] = useState({
    defaultLessonPrice: '',
    stripePublishableKey: '',
    stripeSecretKeySet: false,
    billingCronSecretSet: false,
    bankInfo: {
      bankName: '',
      branchName: '',
      accountType: '',
      accountNumber: '',
      accountHolder: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [newStripeSecretKey, setNewStripeSecretKey] = useState('');
  const [newCronSecret, setNewCronSecret] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data.settings);
    } catch (error) {
      console.error('設定取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);

    try {
      await api.put('/admin/settings', {
        defaultLessonPrice: parseInt(settings.defaultLessonPrice, 10) || 0,
        stripePublishableKey: settings.stripePublishableKey,
        stripeSecretKey: newStripeSecretKey || undefined,
        bankInfo: settings.bankInfo,
        billingCronSecret: newCronSecret || undefined
      });
      setMessage({ type: 'success', text: 'Settings saved.' });
      setNewStripeSecretKey('');
      setNewCronSecret('');
      await fetchSettings();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleBankChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      bankInfo: { ...prev.bankInfo, [field]: value }
    }));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-settings">
      <div className="page-header">
        <h1>決済・振込設定</h1>
        <p className="subtitle">レッスン料金と Stripe 決済情報を管理します</p>
      </div>

      {message && (
        <div
          className={`admin-settings-message ${message.type}`}
          style={{
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            marginBottom: 16,
            background: 'var(--color-white)',
            border: '1px solid var(--color-border)'
          }}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSaveSettings}>
        <div className="settings-card">
          <h2>💰 Lesson Pricing</h2>
          <div className="form-group">
            <label>Default lesson price (PHP / 60 min)</label>
            <input
              type="number"
              value={settings.defaultLessonPrice}
              onChange={(e) => handleChange('defaultLessonPrice', e.target.value)}
              placeholder="1170"
              min="0"
            />
            <p className="hint">Used when a teacher's `hourly_rate` is not set.</p>
          </div>
        </div>

        <div className="settings-card">
          <h2>💳 Stripe 決済設定</h2>

          <div className="form-group">
            <label>Publishable Key</label>
            <input
              type="text"
              value={settings.stripePublishableKey}
              onChange={(e) => handleChange('stripePublishableKey', e.target.value)}
              placeholder="pk_live_..."
            />
            <p className="hint">Stripe.js とカード登録画面で使用する公開鍵です。</p>
          </div>

          <div className="form-group">
            <label>Secret Key {settings.stripeSecretKeySet && <span style={{ color: 'green' }}>✓ 設定済み</span>}</label>
            <input
              type="password"
              value={newStripeSecretKey}
              onChange={(e) => setNewStripeSecretKey(e.target.value)}
              placeholder={settings.stripeSecretKeySet ? '変更する場合のみ入力' : 'sk_live_...'}
            />
            <p className="hint">サーバー側の SetupIntent / PaymentIntent 作成に使う秘密鍵です。</p>
          </div>

          <div className="form-group">
            <label>自動回収シークレット {settings.billingCronSecretSet && <span style={{ color: 'green' }}>✓ 設定済み</span>}</label>
            <input
              type="password"
              value={newCronSecret}
              onChange={(e) => setNewCronSecret(e.target.value)}
              placeholder={settings.billingCronSecretSet ? '変更する場合のみ入力' : 'ランダムな文字列'}
            />
            <p className="hint">Cloud Scheduler の x-cron-secret ヘッダーで使用します。</p>
          </div>
        </div>

        <div className="settings-card">
          <h2>🏦 振込先情報（参照用）</h2>
          <p className="hint" style={{ marginTop: 0 }}>請求書やメールに表示する手動振込先です。</p>

          <div className="form-row">
            <div className="form-group">
              <label>銀行名</label>
              <input
                type="text"
                value={settings.bankInfo.bankName}
                onChange={(e) => handleBankChange('bankName', e.target.value)}
                placeholder="三菱UFJ銀行"
              />
            </div>
            <div className="form-group">
              <label>支店名</label>
              <input
                type="text"
                value={settings.bankInfo.branchName}
                onChange={(e) => handleBankChange('branchName', e.target.value)}
                placeholder="渋谷支店"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>口座種別</label>
              <select
                value={settings.bankInfo.accountType}
                onChange={(e) => handleBankChange('accountType', e.target.value)}
              >
                <option value="">選択してください</option>
                <option value="普通">普通</option>
                <option value="当座">当座</option>
              </select>
            </div>
            <div className="form-group">
              <label>口座番号</label>
              <input
                type="text"
                value={settings.bankInfo.accountNumber}
                onChange={(e) => handleBankChange('accountNumber', e.target.value)}
                placeholder="1234567"
              />
            </div>
          </div>

          <div className="form-group">
            <label>口座名義</label>
            <input
              type="text"
              value={settings.bankInfo.accountHolder}
              onChange={(e) => handleBankChange('accountHolder', e.target.value)}
              placeholder="カ）アイニホンゴ"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      <style>{`
        .admin-settings-message.success {
          color: var(--color-primary);
        }
        .admin-settings-message.error {
          color: var(--color-danger);
        }
        .settings-card {
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 24px;
          margin-bottom: 24px;
        }
        .settings-card h2 {
          margin: 0 0 16px 0;
          font-size: 18px;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          font-weight: 500;
          margin-bottom: 6px;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 14px;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--color-primary);
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .hint {
          margin-top: 6px;
          color: var(--color-text-light);
          font-size: 12px;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default Settings;
